import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';

import { Cell } from '@/components/site/shell';
import {
    addDaysToDateKey,
    cellKey,
    getClueById,
    getClueCells,
    getCluesForCell,
    getFirstInputForClue,
    getInputCells,
    getSolutionLetter,
    lettersMatch,
    nextInputCell,
    normalizeHebrewLetter,
    type CrosswordPayload,
    type TashbetzClue,
} from '@/lib/ashkelon-crossword';
import { HOMEPAGE_ARROWWORD_HASH } from '@/lib/site-url';
import { cn } from '@/lib/utils';
import { loadCrosswordPuzzleFn } from '@/server/functions/crossword';
import { Button } from '@/components/ui/button';

type Entries = Record<string, string>;

function buildEmptyEntries(puzzle: CrosswordPayload): Entries {
    const entries: Entries = {};
    for (const input of getInputCells(puzzle.board)) {
        entries[cellKey(input.row, input.col)] = '';
    }
    return entries;
}

function buildSolutionEntries(puzzle: CrosswordPayload): Entries {
    const entries: Entries = {};
    for (const input of getInputCells(puzzle.board)) {
        entries[cellKey(input.row, input.col)] = input.letter;
    }
    return entries;
}

function isComplete(puzzle: CrosswordPayload, entries: Entries): boolean {
    for (const input of getInputCells(puzzle.board)) {
        const user = entries[cellKey(input.row, input.col)] ?? '';
        if (!lettersMatch(user, input.letter)) return false;
    }
    return true;
}

function defaultStartClue(puzzle: CrosswordPayload): TashbetzClue {
    return getClueById(puzzle.board, 'city') ?? puzzle.board.clues[0]!;
}

export function CrosswordGame({
    puzzle: initialPuzzle,
    canBrowseDays = false,
}: {
    puzzle: CrosswordPayload | null;
    canBrowseDays?: boolean;
}) {
    const [puzzle, setPuzzle] = useState(initialPuzzle);
    const [loadingDay, setLoadingDay] = useState(false);
    const [entries, setEntries] = useState<Entries>(() =>
        initialPuzzle ? buildEmptyEntries(initialPuzzle) : {},
    );
    const [activeCell, setActiveCell] = useState(() => {
        if (!initialPuzzle) return { row: 0, col: 0 };
        const clue = defaultStartClue(initialPuzzle);
        return getFirstInputForClue(clue);
    });
    const [activeClueId, setActiveClueId] = useState(() =>
        initialPuzzle ? defaultStartClue(initialPuzzle).id : '',
    );
    const [completed, setCompleted] = useState(false);
    const activeInputRef = useRef<HTMLInputElement>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const dotStreakRef = useRef(0);
    const dotTimerRef = useRef<number | null>(null);

    useEffect(() => {
        setPuzzle(initialPuzzle);
    }, [initialPuzzle]);

    useEffect(() => {
        if (!puzzle) return;
        const startClue = defaultStartClue(puzzle);
        setEntries(buildEmptyEntries(puzzle));
        setActiveClueId(startClue.id);
        setActiveCell(getFirstInputForClue(startClue));
        setCompleted(false);
    }, [puzzle?.id]);

    const board = puzzle?.board;
    const meta = puzzle?.meta;

    const activeClue = useMemo(() => {
        if (!board) return undefined;
        return getClueById(board, activeClueId) ?? board.clues[0];
    }, [board, activeClueId]);

    const highlightedKeys = useMemo(() => {
        if (!board) return new Set<string>();
        const clue = getClueById(board, activeClueId);
        if (!clue) return new Set<string>();
        return new Set(getClueCells(clue).map(({ row, col }) => cellKey(row, col)));
    }, [board, activeClueId]);

    const focusActiveInput = useCallback(() => {
        requestAnimationFrame(() => {
            activeInputRef.current?.focus({ preventScroll: true });
        });
    }, []);

    const selectClue = useCallback(
        (clue: TashbetzClue) => {
            const first = getFirstInputForClue(clue);
            setActiveClueId(clue.id);
            setActiveCell(first);
            focusActiveInput();
        },
        [focusActiveInput],
    );

    const selectInputCell = useCallback(
        (row: number, col: number) => {
            if (!board) return;
            const clues = getCluesForCell(board, row, col);
            const sameCell = activeCell.row === row && activeCell.col === col;
            setActiveCell({ row, col });
            if (clues.length > 0) {
                const currentIndex = clues.findIndex((clue) => clue.id === activeClueId);
                const next =
                    sameCell && clues.length > 1
                        ? clues[(currentIndex + 1) % clues.length]!
                        : clues.find((clue) => clue.id === activeClueId) ?? clues[0]!;
                setActiveClueId(next.id);
            }
            focusActiveInput();
        },
        [board, activeCell, activeClueId, focusActiveInput],
    );

    const setCellLetter = useCallback((row: number, col: number, value: string) => {
        setEntries((prev) => ({ ...prev, [cellKey(row, col)]: value }));
    }, []);

    const handleLetterInput = useCallback(
        (raw: string) => {
            if (!activeCell || !activeClue) return;
            const letter = normalizeHebrewLetter(raw);
            if (!letter || !/[\u0590-\u05FF]/.test(letter)) return;

            setCellLetter(activeCell.row, activeCell.col, letter);

            const next = nextInputCell(activeCell.row, activeCell.col, activeClue);
            if (next) selectInputCell(next.row, next.col);
        },
        [activeCell, activeClue, selectInputCell, setCellLetter],
    );

    const handleBackspace = useCallback(() => {
        if (!activeCell || !activeClue) return;
        const key = cellKey(activeCell.row, activeCell.col);
        const current = entries[key] ?? '';

        if (current) {
            setCellLetter(activeCell.row, activeCell.col, '');
            return;
        }

        const prev = nextInputCell(activeCell.row, activeCell.col, activeClue, true);
        if (prev) {
            selectInputCell(prev.row, prev.col);
            setCellLetter(prev.row, prev.col, '');
        }
    }, [activeCell, activeClue, entries, selectInputCell, setCellLetter]);

    const revealAnswers = useCallback(() => {
        if (!puzzle) return;
        setEntries(buildSolutionEntries(puzzle));
        dotStreakRef.current = 0;
    }, [puzzle]);

    const registerDotPress = useCallback(() => {
        if (dotTimerRef.current) window.clearTimeout(dotTimerRef.current);
        dotStreakRef.current += 1;
        if (dotStreakRef.current >= 3) {
            revealAnswers();
            return;
        }
        dotTimerRef.current = window.setTimeout(() => {
            dotStreakRef.current = 0;
        }, 1200);
    }, [revealAnswers]);

    const browseDay = useCallback(
        async (delta: number) => {
            if (!canBrowseDays || !meta || loadingDay) return;
            const nextDateKey = addDaysToDateKey(meta.dateKey, delta);
            setLoadingDay(true);
            try {
                const nextPuzzle = await loadCrosswordPuzzleFn({
                    data: { dateKey: nextDateKey },
                });
                if (nextPuzzle) setPuzzle(nextPuzzle);
            } finally {
                setLoadingDay(false);
            }
        },
        [canBrowseDays, meta, loadingDay],
    );

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== '.' || event.metaKey || event.ctrlKey || event.altKey) return;
            const section = sectionRef.current;
            if (!section?.contains(document.activeElement)) return;

            event.preventDefault();
            registerDotPress();
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            if (dotTimerRef.current) window.clearTimeout(dotTimerRef.current);
        };
    }, [registerDotPress]);

    useEffect(() => {
        if (puzzle) setCompleted(isComplete(puzzle, entries));
    }, [entries, puzzle]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (window.location.hash !== `#${HOMEPAGE_ARROWWORD_HASH}`) return;
        sectionRef.current?.scrollIntoView({ block: 'start' });
    }, [puzzle]);

    if (!puzzle || !board || !meta) return null;

    return (
        <section
            ref={sectionRef}
            id={HOMEPAGE_ARROWWORD_HASH}
            className="relative hidden w-full scroll-mt-16 lg:block"
            aria-label="תשחץ אשקלון"
        >
            <Cell>
                <div className="grid-dots" aria-hidden />

                <div className="relative">
                    <div className="flex items-end justify-between gap-4 border-b border-white/10 px-6 py-3">
                        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <h2 className="title-single-line text-lg font-normal tracking-tight text-white md:text-xl">
                                תשחץ אשקלון
                            </h2>
                            <p className="text-sm text-white/55">
                                {meta.dateLabel} · מספר {meta.number}
                            </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            {canBrowseDays ? (
                                <>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="size-8 rounded-none text-white/55 hover:text-white"
                                        aria-label="יום קודם"
                                        disabled={loadingDay}
                                        onClick={() => void browseDay(-1)}
                                    >
                                        <ChevronRight className="size-4" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="size-8 rounded-none text-white/55 hover:text-white"
                                        aria-label="יום הבא"
                                        disabled={loadingDay}
                                        onClick={() => void browseDay(1)}
                                    >
                                        <ChevronLeft className="size-4" />
                                    </Button>
                                </>
                            ) : null}
                            {completed ? (
                                <p className="text-sm text-brand-accent" role="status">
                                    כל הכבוד!
                                </p>
                            ) : null}
                        </div>
                    </div>

                    <div className="w-full px-2 py-3 sm:px-3 sm:py-4 md:px-4 md:py-5">
                        <div
                            className="w-full overflow-hidden border border-white/10 bg-black"
                            dir="ltr"
                            role="grid"
                            aria-label="לוח תשחץ"
                        >
                            <div
                                className="grid w-full gap-px bg-white/10 p-px"
                                style={{
                                    gridTemplateColumns: `repeat(${board.cols}, minmax(0, 1fr))`,
                                }}
                            >
                                {board.cells.map((row, rowIndex) =>
                                    row.map((cell, colIndex) => {
                                        const key = cellKey(rowIndex, colIndex);

                                        if (cell.kind === 'block') {
                                            return (
                                                <div
                                                    key={key}
                                                    className="aspect-square bg-black"
                                                    aria-hidden
                                                />
                                            );
                                        }

                                        if (cell.kind === 'clue') {
                                            const clue = getClueById(board, cell.clueId);
                                            if (!clue) return null;

                                            const isActive = activeClueId === clue.id;

                                            return (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    role="gridcell"
                                                    aria-label={`${clue.text}, ${clue.answer.length} אותיות`}
                                                    onMouseDown={(event) => event.preventDefault()}
                                                    onClick={() => selectClue(clue)}
                                                    className={cn(
                                                        'relative flex aspect-square w-full items-center justify-center overflow-hidden bg-[#2a2a2a] p-0.5 text-center transition-colors lg:pb-4',
                                                        isActive &&
                                                            'bg-brand-accent/15 ring-1 ring-brand-accent/50 ring-inset',
                                                    )}
                                                >
                                                    <span
                                                        dir="rtl"
                                                        className="line-clamp-3 px-0.5 text-[8px] leading-tight text-white/80 sm:px-1 sm:text-[10px] md:text-[11px] xl:text-xs"
                                                    >
                                                        {clue.text}
                                                    </span>
                                                    <span
                                                        className={cn(
                                                            'pointer-events-none absolute bottom-0.5 hidden text-[9px] leading-none text-white/55 sm:text-[11px] lg:bottom-1 lg:block xl:bottom-1.5',
                                                            cell.arrow === 'left'
                                                                ? 'left-0.5 lg:left-1 xl:left-1.5'
                                                                : 'left-1/2 -translate-x-1/2',
                                                        )}
                                                        aria-hidden
                                                    >
                                                        {cell.arrow === 'left' ? '←' : '↓'}
                                                    </span>
                                                </button>
                                            );
                                        }

                                        const isActive =
                                            activeCell.row === rowIndex &&
                                            activeCell.col === colIndex;
                                        const isHighlighted = highlightedKeys.has(key);
                                        const value = entries[key] ?? '';
                                        const solution =
                                            getSolutionLetter(board, rowIndex, colIndex) ?? '';
                                        const isCorrect =
                                            value.length > 0 && lettersMatch(value, solution);

                                        return (
                                            <div
                                                key={key}
                                                role="gridcell"
                                                aria-selected={isActive}
                                                onMouseDown={(event) => {
                                                    event.preventDefault();
                                                    selectInputCell(rowIndex, colIndex);
                                                }}
                                                className={cn(
                                                    'relative flex aspect-square w-full items-center justify-center bg-[#141414] transition-colors',
                                                    isHighlighted && 'bg-brand-accent/[0.07]',
                                                    isActive &&
                                                        'ring-2 ring-brand-accent ring-inset z-[1]',
                                                    !isActive && 'hover:bg-white/[0.04]',
                                                )}
                                            >
                                                {isActive ? (
                                                    <input
                                                        ref={activeInputRef}
                                                        type="text"
                                                        inputMode="text"
                                                        lang="he"
                                                        value={value}
                                                        autoComplete="off"
                                                        autoCorrect="off"
                                                        autoCapitalize="off"
                                                        spellCheck={false}
                                                        aria-label={`תא ${rowIndex + 1},${colIndex + 1}`}
                                                        className="size-full bg-transparent text-center text-base font-normal text-white outline-none sm:text-lg md:text-xl"
                                                        onChange={(event) => {
                                                            const next = event.target.value;
                                                            if (!next) {
                                                                setCellLetter(
                                                                    rowIndex,
                                                                    colIndex,
                                                                    '',
                                                                );
                                                                return;
                                                            }
                                                            handleLetterInput(next);
                                                        }}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Backspace') {
                                                                event.preventDefault();
                                                                handleBackspace();
                                                                return;
                                                            }

                                                            if (
                                                                event.key === 'ArrowRight' ||
                                                                event.key === 'ArrowLeft'
                                                            ) {
                                                                event.preventDefault();
                                                                const reverse =
                                                                    event.key === 'ArrowRight';
                                                                const next = nextInputCell(
                                                                    rowIndex,
                                                                    colIndex,
                                                                    activeClue!,
                                                                    reverse,
                                                                );
                                                                if (next) {
                                                                    selectInputCell(
                                                                        next.row,
                                                                        next.col,
                                                                    );
                                                                }
                                                                return;
                                                            }

                                                            if (
                                                                event.key === 'ArrowDown' ||
                                                                event.key === 'ArrowUp'
                                                            ) {
                                                                event.preventDefault();
                                                                const clues = getCluesForCell(
                                                                    board,
                                                                    rowIndex,
                                                                    colIndex,
                                                                );
                                                                const vertical = clues.find(
                                                                    (clue) =>
                                                                        clue.direction === 'down',
                                                                );
                                                                const horizontal = clues.find(
                                                                    (clue) =>
                                                                        clue.direction ===
                                                                        'across',
                                                                );
                                                                const target =
                                                                    event.key === 'ArrowDown'
                                                                        ? vertical ?? horizontal
                                                                        : horizontal ?? vertical;
                                                                if (
                                                                    target &&
                                                                    target.id !== activeClueId
                                                                ) {
                                                                    selectClue(target);
                                                                }
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <span
                                                        dir="rtl"
                                                        className="text-base font-normal text-white sm:text-lg md:text-xl"
                                                    >
                                                        {value}
                                                    </span>
                                                )}
                                                {isCorrect ? (
                                                    <Check
                                                        className="pointer-events-none absolute end-0.5 top-0.5 size-2.5 text-emerald-400 sm:size-3"
                                                        aria-hidden
                                                    />
                                                ) : null}
                                            </div>
                                        );
                                    }),
                                )}
                            </div>
                        </div>

                        <p className="mt-4 text-sm leading-relaxed text-white/55" role="status">
                            {completed
                                ? 'סיימתם את תשחץ היום. תשחץ חדש מחכה מחר - חזרו אחרי חצות.'
                                : 'לוח חדש בכל בוקר. סיימתם? חזרו מחר לתשחץ הבא.'}
                        </p>
                    </div>
                </div>
            </Cell>
        </section>
    );
}
